import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './EducatorQuestion.css';
import { sanitizeForDisplay } from '../../utils/htmlSanitizer';
import { getTokenForRequest } from '../../utils/apiAuth';
import QuestionReview from '../QuestionReview/QuestionReview';

// Global axios interceptor for 401 Unauthorized
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      // Temporarily disable automatic redirect for debugging
      console.error('401 Unauthorized error:', error.response);
      // window.location.href = '/login.aspx';
      // return;
    }
    return Promise.reject(error);
  }
);

/**
 * Helper function to encode token for API calls using AES encryption
 * @param {string} token - The session token
 * @returns {string} - AES encrypted token
 */
const encodeToken = (token) => {
  return token.toString();
};

/**
 * Helper function to get difficulty level name
 * @param {number} difficulty - The difficulty level number
 * @returns {string} - Descriptive difficulty name
 */
const getDifficultyName = (difficulty) => {
  const difficultyMap = {
    1: 'Beginner',
    2: 'Advanced Beginner',
    3: 'Competent',
    4: 'Expert'
  };
  return difficultyMap[difficulty] || `Level ${difficulty}`;
};

/**
 * EducatorQuestion Component
 * Displays question details with actions for viewing solution and hints
 */
const EducatorQuestion = ({ questionId: propQuestionId, token: propToken, hideComments = false }) => {
  // State management
  const [questionId, setQuestionId] = useState(propQuestionId || null);
  const [token, setToken] = useState(propToken || '');
  const [question, setQuestion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [selectedHint, setSelectedHint] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [reviewMode, setReviewMode] = useState(null);

  // Update state when props change (important for modal updates)
  useEffect(() => {
    if (propQuestionId && propQuestionId !== questionId) {
      console.log('EducatorQuestion: questionId prop changed, updating state', { old: questionId, new: propQuestionId });
      setQuestionId(propQuestionId);
      setQuestion(null); // Clear old question data
      setError(null); // Clear any errors
      setShowSolution(false); // Reset solution visibility
      setShowHints(false); // Reset hints visibility
    }
    if (propToken && propToken !== token) {
      console.log('EducatorQuestion: token prop changed, updating state');
      setToken(propToken);
    }
  }, [propQuestionId, propToken]);

  // Extract questionId and token from URL parameters if not provided as props
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlQuestionId = window.location.pathname.split('/').pop();
    const urlToken = urlParams.get('token');
    
    // Check for review mode from URL hash
    if (window.location.hash) {
      const hashParts = window.location.hash.split('/');
      if (hashParts.length > 1) {
        setReviewMode(hashParts[1]);
        if (hashParts.length > 2 && !propQuestionId) {
          setQuestionId(hashParts[2]);
        }
      }
    }
    
    if (!propQuestionId && urlQuestionId && urlQuestionId !== 'preview') {
      setQuestionId(urlQuestionId);
    }
    
    if (!propToken && urlToken) {
      setToken(urlToken);
    } else if (!propToken) {
      setToken(getTokenForRequest());
    }
  }, [propQuestionId, propToken]);

  // Fetch question data when questionId is available; auth via HttpOnly cookie or token param
  useEffect(() => {
    if (questionId) {
      fetchQuestionData();
    }
  }, [questionId, token]);

  // Helper function to render MathJax for a specific container
  const renderMathJaxForContainer = (container, forceRerender = false) => {
    if (!container || !document.contains(container)) {
      return;
    }

    // Find all math elements within the container
    // Include both processed and unprocessed elements to ensure everything is rendered
    const mathElements = container.querySelectorAll('.mathformula, [data-tex], span[class*="math"]');
    const validElements = Array.from(mathElements).filter(el => el && document.contains(el));

    if (typeof window.MathJax !== 'undefined' && window.MathJax.typesetPromise) {
      try {
        // Always typeset the entire container to ensure all content (question, solution, hints) is processed
        // This ensures that when hints are shown, question and solution math are also re-rendered
        if (container && document.contains(container)) {
          window.MathJax.typesetPromise([container]).then(() => {
            // Success
          }).catch((error) => {
            // Silently handle errors
          });
        } else if (validElements.length > 0) {
          // Fallback: typeset specific elements if container is not valid
          window.MathJax.typesetPromise(validElements).then(() => {
            // Success
          }).catch((error) => {
            // Silently handle errors
          });
        }
      } catch (error) {
        // Silently handle errors
      }
    } else if (typeof window.typesetMathJax === 'function') {
      try {
        // Use helper function - it will handle the container
        if (container && document.contains(container)) {
          window.typesetMathJax(container);
        } else if (validElements.length > 0) {
          window.typesetMathJax(validElements);
        }
      } catch (error) {
        // Silently handle errors
      }
    }
  };

  // Render MathJax when question content is loaded or updated
  useEffect(() => {
    if (!question) return;

    // Function to trigger MathJax rendering
    const renderMathJax = () => {
      // Find the component's root element using the actual className
      const componentRoot = document.querySelector('.educator-question');
      if (!componentRoot) {
        // Try to find by statement content as fallback
        const statementContent = document.querySelector('.statement-content');
        if (!statementContent) {
          return;
        }
      }

      const container = componentRoot || document.body;
      renderMathJaxForContainer(container);
    };

    // Wait for DOM to update, then render MathJax
    // Use multiple timeouts to catch content that loads asynchronously
    const timer1 = setTimeout(renderMathJax, 100);
    const timer2 = setTimeout(renderMathJax, 500);
    const timer3 = setTimeout(renderMathJax, 1000);
    const timer4 = setTimeout(renderMathJax, 2000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [question]);

  // Render MathJax when hints are shown or selected hint changes
  // Also re-render question and solution content to ensure all math is rendered
  useEffect(() => {
    if (!showHints || !selectedHint) return;

    // Function to trigger MathJax rendering for entire question (including hints)
    const renderAllMathJax = () => {
      // Find the entire educator-question container to render all content
      const componentRoot = document.querySelector('.educator-question');
      if (!componentRoot) {
        return;
      }

      renderMathJaxForContainer(componentRoot);
    };

    // Wait for DOM to update, then render MathJax
    // Use multiple timeouts to catch content that loads asynchronously
    const timer1 = setTimeout(renderAllMathJax, 100);
    const timer2 = setTimeout(renderAllMathJax, 300);
    const timer3 = setTimeout(renderAllMathJax, 500);
    const timer4 = setTimeout(renderAllMathJax, 1000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [showHints, selectedHint]);

  // Also render MathJax when solution is shown
  useEffect(() => {
    if (!showSolution) return;

    // Function to trigger MathJax rendering when solution is shown
    const renderSolutionMathJax = () => {
      const componentRoot = document.querySelector('.educator-question');
      if (!componentRoot) {
        return;
      }

      renderMathJaxForContainer(componentRoot);
    };

    // Wait for DOM to update, then render MathJax
    const timer1 = setTimeout(renderSolutionMathJax, 100);
    const timer2 = setTimeout(renderSolutionMathJax, 300);
    const timer3 = setTimeout(renderSolutionMathJax, 500);
    const timer4 = setTimeout(renderSolutionMathJax, 1000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [showSolution]);

  // Watch for hash changes to update review mode
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash) {
        const hashParts = window.location.hash.split('/');
        if (hashParts.length > 1) {
          setReviewMode(hashParts[1]);
          if (hashParts.length > 2 && !propQuestionId) {
            const newQuestionId = hashParts[2];
            if (parseInt(newQuestionId) > 0) {
              setQuestionId(newQuestionId);
            }
          }
        }
      }
    };

    // Initial check
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [propQuestionId]);

  /**
   * Fetches question data from the API
   */
  const fetchQuestionData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Using jQuery.ajax equivalent format - GET request with query parameters
      const response = await axios({
        url: `/webservices/Question.svc/Basic?token=${encodeToken(token)}&questionId=${questionId}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      });

      if (response.data) {
        setQuestion(response.data);
      } else {
        setError('Question not found');
      }
    } catch (err) {
      console.error('Error fetching question:', err);
      if (err.response && err.response.status === 401) {
        setError('Your session has expired. Please refresh the page and login again.');
      } else {
        setError('Failed to load question data: ' + (err.message || 'Unknown error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles the "Learn Solution" button click
   */
  const handleLearnSolution = () => {
    setShowSolution(true);
  };

  /**
   * Handles the "Hint" button click
   */
  const handleShowHints = () => {
    setShowHints(true);
    
    // If there are scaffolds available, automatically show the first text hint
    if (question.Scaffolds && question.Scaffolds.length > 0) {
      // Find the first text hint (not video or solution video)
      const firstTextHint = question.Scaffolds.find(scaffold => 
        scaffold.FileType !== 'Video' && scaffold.Desc
      );
      
      if (firstTextHint) {
        setSelectedHint(firstTextHint);
      } else {
        // If no text hints, show the first available hint (could be video)
        setSelectedHint(question.Scaffolds[0]);
      }
    }
  };

  /**
   * Handles selecting a specific hint
   * @param {Object} hint - The hint/scaffold object
   */
  const handleHintClick = (hint) => {
    setSelectedHint(hint);
  };

  /**
   * Opens video modal with specified video
   * @param {Object} video - The video object with FilePath and other details
   */
  const openVideoModal = (video) => {
    setCurrentVideo(video);
    setShowVideoModal(true);
  };

  /**
   * Closes the video modal
   */
  const closeVideoModal = () => {
    setShowVideoModal(false);
    setCurrentVideo(null);
  };

  /**
   * Renders the question choices for MCQ/MRQ questions
   * @param {Array} choices - Array of choice objects
   * @returns {JSX.Element}
   */
  const renderChoices = (choices) => {
    if (!choices || choices.length === 0) return null;

    return (
      <div className="question-choices">
        {choices.map((choice, index) => (
          <div 
            key={choice.ID || index} 
            className={`choice-item ${showSolution && choice.IsCorrect ? 'correct-choice' : ''}`}
          >
            <span className="choice-label">
              {String.fromCharCode(65 + index)}.
            </span>
            <span 
              className="choice-text"
              dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(choice.Desc || choice.Description || '') }}
            />
            {choice.AdditionalFile && choice.AdditionalFile.Type === 'image' && (
              <div className="choice-image">
                <img 
                  src={`https://d6adgef862n1y.cloudfront.net/${choice.AdditionalFile.FilePath}`}
                  alt="Choice attachment"
                  className="img-fluid"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  /**
   * Renders the question answers for FBQ questions
   * @param {Array} answers - Array of answer objects
   * @returns {JSX.Element}
   */
  const renderAnswers = (answers) => {
    if (!answers || answers.length === 0) return null;

    return (
      <div className="question-answers">
        <div className="answer-title">Correct Answers:</div>
        {answers.map((answer, index) => (
          <div key={answer.ID || index} className="answer-item">
            {answer.TextAnswer && (
              <span className="text-answer">{answer.TextAnswer}</span>
            )}
            {answer.NumRangeMin !== undefined && (
              <span className="numeric-answer">
                {answer.NumRangeMin}
                {answer.NumRangeMax !== answer.NumRangeMin && (
                  <> ~ {answer.NumRangeMax}</>
                )}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  /**
   * Renders scaffolds/hints
   * @param {Array} scaffolds - Array of scaffold objects
   * @returns {JSX.Element}
   */
  const renderScaffolds = (scaffolds) => {
    if (!scaffolds || scaffolds.length === 0) {
      return <div className="no-hints">No hints available for this question.</div>;
    }

    return (
      <div className="scaffolds-list">
        {scaffolds.map((scaffold, index) => (
          <div 
            key={scaffold.ID || index} 
            className="scaffold-item"
            onClick={() => handleHintClick(scaffold)}
          >
            <div className="scaffold-header">
              <span className="scaffold-type">
                {scaffold.FileType === 'Video' ? '📹' : '💡'} 
                {scaffold.FileType === 'Video' ? 'Video Hint' : 'Text Hint'}
              </span>
            </div>
            {scaffold.FileType === 'Video' ? (
              <div className="scaffold-video">
                <div className="video-info">
                  <div className="video-name">{scaffold.CamtasiaName || 'Video Hint'}</div>
                  <div className="video-description">{scaffold.Desc}</div>
                </div>
              </div>
            ) : (
              <div 
                className="scaffold-text"
                dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(scaffold.Desc || '') }}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="educator-question loading">
        <div className="loading-spinner">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading question...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="educator-question error">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            Please check the question ID and try again, or contact support if the problem persists.
          </p>
        </div>
      </div>
    );
  }

  // No question data state
  if (!question) {
    return (
      <div className="educator-question no-question">
        <div className="alert alert-info" role="alert">
          <h4 className="alert-heading">No Question Selected</h4>
          <p>Please provide a valid question ID to display question details.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="educator-question">
      <div className="container-fluid">
        <div className="row">
          {/* Left Side - Question Content */}
          <div className="col-12 col-md-8 question-content">

            <div className="question-body">
              {/* Question Statement */}
              <div className="question-statement">
                <h4>Question:</h4>
                <div 
                  className="statement-content"
                  dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(question.Statement || '') }}
                />
                
                {/* Question Image/File */}
                {question.StatementFile && question.StatementFile.Type === 'image' && (
                  <div className="question-image">
                    <img 
                      src={`https://d6adgef862n1y.cloudfront.net/${question.StatementFile.FilePath}`}
                      alt="Question attachment"
                      className="img-fluid"
                    />
                  </div>
                )}
              </div>

              {/* Question Choices for MCQ/MRQ */}
              {(question.QuestionType === 'MCQ' || question.QuestionType === 'MRQ') && question.Choices && (
                <div className="question-choices-section">
                  <h4>Options:</h4>
                  {renderChoices(question.Choices)}
                </div>
              )}

              {/* Question Solution */}
              {showSolution && (
                <div className="question-solution">
                  <h4>Solution:</h4>
                  
                  {/* Show correct answers */}
                  {question.QuestionType === 'FBQ' && question.Answers && (
                    renderAnswers(question.Answers)
                  )}
                  
                  {(question.QuestionType === 'MCQ' || question.QuestionType === 'MRQ') && question.Choices && (
                    <div className="correct-choices">
                      <div className="answer-title">Correct Answer(s):</div>
                      {question.Choices.filter(choice => choice.IsCorrect).map((choice, index) => (
                        <div key={choice.ID || index} className="correct-choice-item">
                          <span 
                            className="choice-text"
                            dangerouslySetInnerHTML={{ __html: choice.Desc || choice.Description }}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show solution videos */}
                  {question.Scaffolds && question.Scaffolds.filter(s => s.VideoType === 'Q' && s.FileType === 'Video').length > 0 && (
                    <div className="solution-videos">
                      <div className="video-title">Solution Videos:</div>
                      {question.Scaffolds.filter(s => s.VideoType === 'Q' && s.FileType === 'Video').map((video, index) => (
                        <div key={video.ID || index} className="solution-video">
                          {video.FilePath && (
                            <div style={{ marginTop: '10px' }}>
                              <button 
                                className="btn btn-success btn-sm"
                                onClick={() => openVideoModal(video)}
                                style={{ background: '#28a745', border: 'none', padding: '8px 16px', borderRadius: '4px', color: 'white', cursor: 'pointer' }}
                              >
                                🎥 Watch Solution Video
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show explanation */}
                  {question.Explanation && (
                    <div className="solution-explanation">
                      <div className="explanation-title">Explanation:</div>
                      <div 
                        className="explanation-content"
                        dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(question.Explanation || '') }}
                      />
                    </div>
                  )}

                  {/* Show explanation file */}
                  {question.ExplanationFile && question.ExplanationFile.Type === 'image' && (
                    <div className="explanation-image">
                      <img 
                        src={`https://d6adgef862n1y.cloudfront.net/${question.ExplanationFile.FilePath}`}
                        alt="Solution explanation"
                        className="img-fluid"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Actions */}
          <div className="col-12 col-md-4 question-actions">
            <div className="actions-panel">
              {/* Learn Solution Button */}
              <div className="d-grid gap-2">
                <button 
                  className="btn btn-primary action-button learn-solution"
                  onClick={handleLearnSolution}
                >
                  <i className="fas fa-lightbulb"></i>
                  Learn Solution
                </button>
              </div>

              {/* Hint Button */}
              <div className="d-grid gap-2">
                <button 
                  className="btn btn-info action-button show-hints"
                  onClick={handleShowHints}
                >
                  <i className="fas fa-question-circle"></i>
                  Hint
                </button>
              </div>

              {/* Hints Section */}
              {showHints && (
                <div className="hints-section">
                  <h4 className="hints-title">Hints</h4>
                  
                  {/* Show selected hint immediately if available */}
                  {selectedHint && (
                    <div className="current-hint" style={{ marginBottom: '20px', background: 'rgba(255, 193, 7, 0.1)', padding: '15px', borderRadius: '8px', border: '1px solid #ffc107' }}>
                      <div style={{ marginBottom: '10px' }}>
                        <strong style={{ color: '#856404' }}>
                          {selectedHint.FileType === 'Video' ? '📹 Video Hint' : '💡 Text Hint'}
                        </strong>
                      </div>
                      {selectedHint.FileType === 'Video' ? (
                        <div className="hint-video">
                          <div className="video-name">{selectedHint.CamtasiaName || 'Video Hint'}</div>
                          <div className="video-description">{selectedHint.Desc}</div>
                          {selectedHint.FilePath && (
                            <div style={{ marginTop: '10px' }}>
                              <button 
                                className="btn btn-primary btn-sm"
                                onClick={() => openVideoModal(selectedHint)}
                                style={{ background: '#007bff', border: 'none', padding: '8px 16px', borderRadius: '4px', color: 'white', cursor: 'pointer' }}
                              >
                                🎥 Play Video
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div 
                          className="hint-text"
                          style={{ color: '#333', lineHeight: '1.5' }}
                          dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(selectedHint.Desc || '') }}
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Show other available hints if there are multiple */}
                  {question.Scaffolds && question.Scaffolds.filter(s => s.VideoType !== 'Q').length > 1 && (
                    <div>
                      <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                        Other available hints:
                      </div>
                      <div className="other-hints">
                        {question.Scaffolds.filter(scaffold => scaffold !== selectedHint && scaffold.VideoType !== 'Q').map((scaffold, index) => (
                          <div 
                            key={scaffold.ID || index} 
                            className="scaffold-item"
                            style={{ 
                              background: 'rgba(255, 255, 255, 0.9)', 
                              color: '#2c3e50',
                              padding: '10px',
                              marginBottom: '8px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: '1px solid #e9ecef'
                            }}
                            onClick={() => setSelectedHint(scaffold)}
                          >
                            <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '5px' }}>
                              {scaffold.FileType === 'Video' ? '📹 Video Hint' : '💡 Text Hint'}
                            </div>
                            {scaffold.FileType === 'Video' ? (
                              <div style={{ fontSize: '14px' }}>{scaffold.CamtasiaName || 'Video Hint'}</div>
                            ) : (
                              <div 
                                style={{ fontSize: '14px' }}
                                dangerouslySetInnerHTML={{ 
                                  __html: sanitizeForDisplay(
                                    (scaffold.Desc || '').substring(0, 100) + 
                                    (scaffold.Desc && scaffold.Desc.length > 100 ? '...' : '')
                                  )
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show message if no hints available */}
                  {(!question.Scaffolds || question.Scaffolds.filter(s => s.VideoType !== 'Q').length === 0) && (
                    <div style={{ textAlign: 'center', color: '#666', fontStyle: 'italic', padding: '15px' }}>
                      No hints available for this question.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Question Review Section */}
        {!hideComments && questionId && (
          <div className="row" style={{ marginTop: '30px' }}>
            <div className="col-12">
              <QuestionReview 
                questionId={questionId} 
                token={token} 
                reviewMode={reviewMode}
              />
            </div>
          </div>
        )}
      </div>

      {/* Video Modal */}
      {showVideoModal && currentVideo && (
        <div 
          className="video-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1050
          }}
          onClick={closeVideoModal}
        >
          <div 
            className="video-modal-content"
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '90%',
              maxHeight: '90%',
              width: '800px',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h4 style={{ margin: 0, color: '#333' }}>
                {currentVideo.CamtasiaName || 'Video'}
              </h4>
              <button 
                onClick={closeVideoModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Video Description */}
            {currentVideo.Desc && (
              <div 
                style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}
                dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(currentVideo.Desc || '') }}
              />
            )}
            
            {/* Video Player */}
            <div style={{ width: '100%', textAlign: 'center' }}>
              <video 
                controls 
                style={{ 
                  width: '100%', 
                  maxHeight: '500px',
                  borderRadius: '4px'
                }}
                preload="metadata"
              >
                <source src={currentVideo.FilePath} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            
            {/* Modal Footer */}
            <div style={{ marginTop: '15px', textAlign: 'right' }}>
              <button 
                className="btn btn-secondary"
                onClick={closeVideoModal}
                style={{
                  background: '#6c757d',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EducatorQuestion; 